import React, { useEffect, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import ConfirmModal from './ConfirmModal';
import KnobField from './KnobField';
import MemberRow from './MemberRow';
import SaveDiffModal from './SaveDiffModal';
import { showToast } from '../toasts/index.jsx';
import { getClassNames } from '../../tools/helpers';
import { displayValue, leafAt, structuredLeaves } from './format';
import {
  fetchScope, saveScope, setMember, setParticipation,
} from './queries/configuration';
import './ConfigurationScope.scss';

/**
 * Renders one value the way its field renders it, so the confirmation lists
 * what the operator saw rather than what the wire carries — plus the empty
 * case, which a field shows as a blank input and a diff has to name.
 *
 * @param {*} value
 * @returns {string}
 */
function readable(value) {
  if (value === null || value === undefined) return 'not set';

  return displayValue(value);
}

/**
 * Every value that differs between what was loaded and what is on screen,
 * flattened so a structured knob reports one line per changed member rather
 * than one line of unreadable object.
 *
 * @param {Array<Object>} knobs - the knobs as loaded.
 * @param {Object} edits - key => edited value.
 * @returns {Array<{label: string, from: string, to: string}>}
 */
function changeList(knobs, edits) {
  const changes = [];

  knobs.forEach((knob) => {
    if (!(knob.key in edits)) return;

    const next = edits[knob.key];

    if (knob.type === 'structured') {
      // Leaf by leaf, not member by member: a member can hold a group of values
      // (the per-car-type costs), and reporting one as a line would put
      // "[object Object]" in the confirmation an operator is meant to read
      // before writing to the database.
      structuredLeaves(next).forEach((leaf) => {
        const before = leafAt(knob.value, leaf.path);
        if (leaf.value === before) return;

        changes.push({
          label: `${knob.label}, ${leaf.path.join(' ')}`,
          from: readable(before),
          to: readable(leaf.value),
        });
      });
      return;
    }

    if (next !== knob.value) {
      changes.push({ label: knob.label, from: readable(knob.value), to: readable(next) });
    }
  });

  return changes;
}

// Which knobs belong under which heading, in the order the approved mockup
// shows them. The catalogue is a flat hash — it knows what each knob means but
// not what an operator is doing when they open this page — so the grouping
// lives here, next to the screen it organizes.
//
// Advanced is collapsed by default because its two knobs change how the solver
// searches rather than what it is allowed to do. Someone tuning Cape Town's
// ride limits should not have to scroll past them.
// Exported so a test can assert it covers every knob in the catalogue. A knob
// added to Gmpro::ConfigKnob::ALL without an entry here is filtered out and
// simply never appears on the page — no error, nothing in a log, just a setting
// that silently cannot be edited.
export const SECTIONS = [
  { title: 'Schedule', keys: ['daily_submit_cron', 'submit_lead_days', 'service_day_start_hour'] },
  {
    title: 'Grouping limits',
    keys: [
      'route_duration_limit', 'max_pickup_to_delivery', 'boarding_buffer_minutes',
      'home_pickup_waiting_time',
    ],
  },
  {
    title: 'Vehicle rules',
    keys: ['vehicle_fixed_cost_by_type', 'vehicle_fixed_cost', 'vehicle_variable_costs'],
  },
  { title: 'Advanced', keys: ['solver_timeout', 'solver_search_mode'], collapsed: true },
];

/**
 * One section of knobs, with its heading. Advanced starts collapsed and can be
 * opened; the others have no reason to close.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {Array<Object>} props.knobs - the knobs belonging to this section.
 * @param {boolean} props.collapsible
 * @param {string} props.baseline - what the layer above is called.
 * @param {function(string, *): void} props.onChange
 * @returns {JSX.Element}
 */
function Section({
  title, knobs, collapsible, baseline, onChange,
}) {
  const [open, setOpen] = useState(!collapsible);

  if (knobs.length === 0) return null;

  return (
    <section className="gmpro-scope__section" data-testid={`gmpro-scope-section-${title.toLowerCase().replaceAll(' ', '-')}`}>
      {collapsible ? (
        <button
          type="button"
          className="gmpro-scope__section-toggle"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {title}
        </button>
      ) : (
        <h3 className="gmpro-scope__section-title">{title}</h3>
      )}

      {open && knobs.map((knob) => (
        <KnobField key={knob.key} knob={knob} baseline={baseline} onChange={onChange} />
      ))}
    </section>
  );
}

/**
 * Screen 2 of the rebuilt GMPRO configuration page: one scope in full.
 *
 * Everything on this page is regional. Company-level configuration is
 * deliberately absent — a company inherits its region's settings, and the only
 * per-company decision here is whether it takes part at all, which is the
 * Companies section rather than a second set of knobs.
 *
 * The Global page differs in two ways, both because it is the bottom layer:
 * it has no participation switch to throw, and what its fields fall back to is
 * the catalogue default rather than a layer above — so its comparison lines say
 * "Default" where a region's say "Global".
 *
 * @param {Object} props
 * @param {string} props.scope - "global" or a collective id, from the URL.
 * @returns {JSX.Element}
 */
export default function ConfigurationScope({ scope }) {
  const [detail, setDetail] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [edits, setEdits] = useState({});
  // Handed to every modal on this page so the dialog centres on the content
  // rather than on the window — the admin sidebar otherwise pulls it off-centre.
  // Modal does the measuring; this page only says what to measure.
  const contentRef = useRef(null);
  // One pending confirmation at a time: { kind, ...payload }. Null means no
  // modal is open. Every mutation on this page goes through one of these, so
  // nothing here writes to the database without a stated consequence first.
  const [pendingAction, setPendingAction] = useState(null);
  const [inFlight, setInFlight] = useState(false);

  useEffect(() => {
    let mounted = true;

    fetchScope(scope)
      .then((payload) => {
        if (mounted) setDetail(payload);
      })
      .catch(() => {
        if (mounted) setLoadFailed(true);
      });

    return () => {
      mounted = false;
    };
  }, [scope]);

  /**
   * Runs a confirmed mutation: in-flight while it goes, then a toast either
   * way, then the server's own view of the scope replaces local state — so
   * what is on screen afterwards is what was actually stored, not what was
   * hoped for.
   *
   * Every mutation endpoint answers with the scope's fresh detail payload, the
   * same thing a GET returns, so its response IS the new state. Re-fetching
   * after it would double the round trips and recompute Gmpro::ScopeSummary a
   * second time — and that walks every regional collective, not just this one.
   *
   * Only a SAVE clears the pending edits — they are what it just persisted.
   * The instant switches share this helper but must not share that: an
   * operator three knob-edits deep who flips a member switch would lose all
   * three without a word, the save bar simply vanishing.
   *
   * @param {function(): Promise<Object>} request - resolves to the fresh detail.
   * @param {string} success - the toast on success.
   * @param {string} failure - the toast on failure.
   * @param {Object} [options]
   * @param {boolean} [options.clearEdits] - true only on the save path.
   * @returns {void}
   */
  function run(request, success, failure, { clearEdits = false } = {}) {
    setInFlight(true);

    request()
      .then((fresh) => {
        setDetail(fresh);
        if (clearEdits) setEdits({});
        showToast('success', success);
      })
      .catch((error) => {
        // The server's refusal sentence (an invariant, or "another admin changed
        // this scope") beats the generic line when it exists.
        const serverSentence = error?.response?.data?.errors?.[0];
        showToast('error', serverSentence || failure);
      })
      .then(() => {
        setInFlight(false);
        setPendingAction(null);
      });
  }

  if (loadFailed) {
    return (
      <p className="gmpro-scope__load-error" data-testid="gmpro-scope-error">
        Couldn&rsquo;t load this configuration. Reload the page to try again.
      </p>
    );
  }

  if (!detail) return null;

  const isGlobal = detail.scope === 'global';
  const baseline = isGlobal ? 'Default' : 'Global';
  const byKey = Object.fromEntries(detail.knobs.map((knob) => [knob.key, knob]));

  // An edited knob renders what the operator typed, not what the server last
  // sent, so a field does not snap back under them while they work.
  const knobFor = (key) => (
    key in edits ? { ...byKey[key], value: edits[key] } : byKey[key]
  );

  function handleChange(key, value) {
    setEdits({ ...edits, [key]: value });
  }

  const changes = changeList(detail.knobs, edits);
  const running = detail.members.filter((member) => member.held_by === null).length;

  return (
    <div className="gmpro-scope" data-testid="gmpro-scope" ref={contentRef}>
      <ToastContainer />

      <header className="gmpro-scope__header">
        <div className="gmpro-scope__identity">
          <h2 className="gmpro-scope__name">{detail.name}</h2>
          <p className="gmpro-scope__status" data-testid="gmpro-scope-status">{detail.status_line}</p>
        </div>

        {/*
          Global has no participation switch. It is the layer every region
          inherits from, not a thing that runs, so there is nothing to enable.
        */}
        {!isGlobal && (
          <div className="gmpro-scope__toggle">
            <span>Enabled</span>
            <button
              type="button"
              role="switch"
              aria-checked={detail.gmpro_enabled}
              aria-label={`GMPRO enabled for ${detail.name}`}
              className={getClassNames('gmpro-member__switch', {off: !detail.gmpro_enabled})}
              onClick={() => setPendingAction({ kind: 'participation', next: !detail.gmpro_enabled })}
              data-testid="gmpro-scope-participation"
            >
              <i />
            </button>
          </div>
        )}
      </header>

      {!isGlobal && (
        <section className="gmpro-scope__section">
          <h3 className="gmpro-scope__section-title">Companies</h3>
          <ul className="gmpro-scope__members">
            {detail.members.map((member) => (
              <MemberRow
                key={member.company_id}
                member={member}
                onToggle={(companyId, next) => setPendingAction({
                  kind: 'member', companyId, next, name: member.name,
                })}
              />
            ))}
          </ul>
        </section>
      )}

      {SECTIONS.map((section) => (
        <Section
          key={section.title}
          title={section.title}
          collapsible={Boolean(section.collapsed)}
          baseline={baseline}
          onChange={handleChange}
          knobs={section.keys.filter((key) => byKey[key]).map(knobFor)}
        />
      ))}

      {/*
        The save bar only exists when there is something to save. A permanently
        present Save button trains an operator to press it without looking,
        which is the habit the diff modal is there to interrupt.
      */}
      {changes.length > 0 && (
        <div className="gmpro-scope__savebar" data-testid="gmpro-scope-savebar">
          <span>
            {changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}
          </span>
          <button
            type="button"
            className="gmpro-scope__save"
            onClick={() => setPendingAction({ kind: 'save' })}
            data-testid="gmpro-scope-save"
          >
            Review and save
          </button>
        </div>
      )}

      {pendingAction && pendingAction.kind === 'save' && (
        <SaveDiffModal
          changes={changes}
          pending={inFlight}
          contentRef={contentRef}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => run(
            () => saveScope(scope, edits, detail.configuration_digest),
            `Saved ${changes.length} ${changes.length === 1 ? 'change' : 'changes'} to ${detail.name}.`,
            `Couldn't save ${detail.name}. Nothing was changed.`,
            { clearEdits: true },
          )}
        />
      )}

      {pendingAction && pendingAction.kind === 'participation' && (
        <ConfirmModal
          title={pendingAction.next
            ? `Enable GMPRO for ${detail.name}?`
            : `Disable GMPRO for ${detail.name}?`}
          body={pendingAction.next
            ? `Its ${running} running ${running === 1 ? 'company' : 'companies'} will be optimised again, and any schedule set here starts firing.`
            : `Its ${running} running ${running === 1 ? 'company' : 'companies'} will not be optimised, and its schedule stops firing until you re-enable it.`}
          confirmLabel={pendingAction.next ? `Enable ${detail.name}` : `Disable ${detail.name}`}
          tone={pendingAction.next ? undefined : 'danger'}
          pending={inFlight}
          contentRef={contentRef}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => run(
            () => setParticipation(scope, pendingAction.next),
            pendingAction.next ? `${detail.name} is enabled.` : `${detail.name} is disabled.`,
            `Couldn't change ${detail.name}. Nothing was changed.`,
          )}
        />
      )}

      {pendingAction && pendingAction.kind === 'member' && (
        <ConfirmModal
          title={pendingAction.next
            ? `Include ${pendingAction.name} in ${detail.name} runs?`
            : `Switch ${pendingAction.name} off in ${detail.name}?`}
          body={pendingAction.next
            ? `${pendingAction.name}'s trips will be included the next time ${detail.name} runs.`
            : `${pendingAction.name}'s trips will be left out of every ${detail.name} run until you switch it back on. Its existing trips are not affected.`}
          confirmLabel={pendingAction.next ? `Include ${pendingAction.name}` : `Switch off ${pendingAction.name}`}
          tone={pendingAction.next ? undefined : 'danger'}
          pending={inFlight}
          contentRef={contentRef}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => run(
            () => setMember(scope, pendingAction.companyId, pendingAction.next),
            pendingAction.next
              ? `${pendingAction.name} is included.`
              : `${pendingAction.name} is switched off.`,
            `Couldn't change ${pendingAction.name}. Nothing was changed.`,
          )}
        />
      )}
    </div>
  );
}
